'use client';

import { OpenApiError } from '@repo/api-clients/utils';
import { Skeleton } from '@repo/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { APP_REVALIDATE_QUERY_NAMES } from '@/constants';
import { type ProfileResponseType, type WalletResponseType } from '@/lib/fetch';
import { getProfileForClient } from '@/server-actions/profile';
import { getConvertedUserBalanceOnClient } from '@/server-actions/user-balance';
import Balance from './index';

interface BalanceWrapperProps {
  initialBalance: WalletResponseType | null;
  initialProfile: ProfileResponseType | null;
}

const BalanceWrapper = ({ initialBalance, initialProfile }: BalanceWrapperProps) => {
  const t = useTranslations('header');

  const { data: balance, isLoading: balanceLoading } = useQuery({
    queryKey: [APP_REVALIDATE_QUERY_NAMES.WALLET_BALANCE],
    queryFn: async () => {
      const { data, error } = await getConvertedUserBalanceOnClient();
      if (error) throw new OpenApiError(error);
      return data ?? null;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - keep in cache even when component unmounts
    refetchOnWindowFocus: false,
    initialData: initialBalance,
  });

  const { data: profile } = useQuery({
    queryKey: [APP_REVALIDATE_QUERY_NAMES.PROFILE],
    queryFn: () => getProfileForClient(),
    staleTime: 10 * 60 * 1000, // 10 minutes - keep in cache even when component unmounts
    refetchOnWindowFocus: false,
    initialData: initialProfile,
  });

  if (balanceLoading) {
    return <Skeleton className="h-full w-20 rounded vt-containers-settings-box-light-bg" />;
  }

  if (!balance) {
    return (
      <div className="flex h-full items-center justify-center whitespace-nowrap p-3 vt-containers-settings-box-light-bg">
        <span>{t('no-balance')}</span>
      </div>
    );
  }

  const selectedSecondaryCurrency = balance.currencies?.find((currency) => currency.type === 'SECONDARY')?.currencyCode;

  return (
    <Balance
      balance={balance}
      profile={profile ?? null}
      selectedSecondaryCurrency={selectedSecondaryCurrency!}
    />
  );
};

export default BalanceWrapper;
